import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHeaders } from '../listPageStyles';
import { StatusBadge } from '../ui';
import { Section, Icon, SubModal } from '../detail/DetailShell';

const API = '/api/RepresentativeIdentity';

const pick = (o, ...ks) => { if (!o) return undefined; for (const k of ks) if (o[k] != null) return o[k]; return undefined; };
const unwrap = (p) => pick(p, 'Data', 'data') ?? p;

/**
 * "Bağlı Tüccarlar" sekmesi (spec §5).
 *
 * ── NE ÇÖZÜYOR ────────────────────────────────────────────────────────────────────────────────
 * Beş temsilci aynı VKN, aynı entegratör hesabı, aynı kaşeyle çalışabilir. Bu bilgiyi beş kez
 * girmek hem hata kaynağı hem bakım kâbusu (spec §5). Burada ayar TEK yerde (ana tüccar) durur;
 * diğerleri ona BAĞLANIR ve AYNI profili gösterir — kopya yok, yayma job'ı yok.
 *
 * ── EKRAN, KARTIN ROLÜNE GÖRE İKİYE AYRILIR ───────────────────────────────────────────────────
 * ANA:  bağlı alt tüccarlar + bağlanabilecekler listesi (seçip bağla).
 * ALT:  hangi anaya bağlı + aynı ana altındaki kardeşler (salt izleme).
 */
export function LinkedTuccarsTab({ repId, repName, canEdit, onToast, onHierarchyChange, refreshKey = 0 }) {
  const navigate = useNavigate();
  const [hier, setHier] = useState({ isPrimaryIdentity: true, linked: [] });
  const [linkable, setLinkable] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmLink, setConfirmLink] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(null);

  const load = useCallback(async () => {
    if (!repId) { setLoading(false); return; }
    setLoading(true);
    try {
      const hRes = await fetch(`${API}/linked-tuccars/${repId}`, { headers: authHeaders() });
      const h = unwrap(await hRes.json().catch(() => null)) || { isPrimaryIdentity: true, linked: [] };
      setHier(h);
      onHierarchyChange?.(h);

      if (h.isPrimaryIdentity) {
        const lRes = await fetch(`${API}/linkable-tuccars/${repId}`, { headers: authHeaders() });
        setLinkable(unwrap(await lRes.json().catch(() => null)) || []);
      } else {
        setLinkable([]);
      }
      setSelected({});
    } catch {
      onToast?.('error', 'Bağlı tüccar bilgisi alınamadı');
    } finally {
      setLoading(false);
    }
    // refreshKey: "Ana Ticari Kimlik" anahtarı GENEL BİLGİLER sekmesinde durur (kullanıcı isteği).
    // Oradan bağ koparıldığında bu sekmenin verisi bayatlar — parent sayacı artırır, biz tazeleriz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repId, refreshKey]);

  useEffect(() => { load(); }, [load]);

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const doLink = async () => {
    setConfirmLink(false);
    setBusy(true);
    try {
      const res = await fetch(`${API}/linked-tuccars/${repId}`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ repIds: selectedIds }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Bağlama başarısız');
      onToast?.('success', pick(body, 'Message', 'message') || 'Tüccarlar bağlandı');
      await load();
    } catch (err) { onToast?.('error', err.message); } finally { setBusy(false); }
  };

  const doUnlink = async () => {
    const sub = confirmUnlink;
    setConfirmUnlink(null);
    setBusy(true);
    try {
      const res = await fetch(`${API}/linked-tuccars/${repId}/${sub.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(pick(body, 'Message', 'message') || 'Bağ çözülemedi');
      onToast?.('success', pick(body, 'Message', 'message') || 'Bağ çözüldü');
      await load();
    } catch (err) { onToast?.('error', err.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="dps-form"><Section icon="user" title="Yükleniyor…" /></div>;

  const isPrimary = hier.isPrimaryIdentity;

  return (
    <div className="dps-form">
      <Section
        icon="user"
        title="Bağlı Tüccarlar"
        subtitle="Aynı ticari kimlik (VKN) altında çalışan temsilciler"
      >
        {isPrimary ? (
          <>
            <div className="dps-inherit" style={{ background: '#FBF1DA', borderColor: '#EEDDB4', color: '#8A5F12' }}>
              <Icon name="info" size={16} />
              <div>
                Aşağıda bağladığınız temsilciler, <strong>bu karttaki firma ve e-belge ayarlarıyla</strong> belge
                basacak ve GİB entegrasyonlarını bu bilgilerle yürütecektir.
              </div>
            </div>

            <p className="dps-sub-lbl">
              Bu ana kimliğe bağlı tüccarlar <span>— {hier.linked.length} temsilci</span>
            </p>
            {hier.linked.length === 0 ? (
              <p className="dps-tbl-empty">Bağlı alt tüccar yok. Aşağıdan seçip bağlayabilirsiniz.</p>
            ) : (
              <table className="dps-tbl">
                <thead>
                  <tr><th>Temsilci</th><th>Kod</th><th>Plaka</th><th>Bölge Müd.</th><th>Durum</th><th /></tr>
                </thead>
                <tbody>
                  {hier.linked.map((r) => (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td className="mono">{r.textId || '—'}</td>
                      <td className="mono">{r.plaka || '—'}</td>
                      <td>{r.vendorName || '—'}</td>
                      <td><StatusBadge tone="ok">Bağlı</StatusBadge></td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="dps-btn ghost sm" onClick={() => navigate(`/representatives/${r.id}`)}>Karta git ↗</button>
                        {canEdit && (
                          <button className="dps-btn danger sm" style={{ marginLeft: 6 }} disabled={busy}
                                  onClick={() => setConfirmUnlink(r)}>Bağı çöz</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {canEdit && (
              <>
                <p className="dps-sub-lbl" style={{ marginTop: 22 }}>
                  Bağlanabilecek tüccarlar <span>— henüz bir ana kimliğe bağlı değil</span>
                </p>
                {linkable.length === 0 ? (
                  <p className="dps-tbl-empty">
                    Bağlanabilecek tüccar yok. Bir temsilcinin burada görünmesi için tipi <strong>Tüccar</strong>{' '}
                    olmalı ve başka bir ana kimliğe bağlı olmamalıdır.
                  </p>
                ) : (
                  <>
                    <table className="dps-tbl">
                      <thead>
                        <tr>
                          <th style={{ width: 44 }} />
                          <th>Temsilci</th><th>Kod</th><th>Plaka</th><th>Ticari Ünvan</th><th>VKN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linkable.map((r) => (
                          <tr key={r.id} onClick={() => setSelected((s) => ({ ...s, [r.id]: !s[r.id] }))}
                              style={{ cursor: 'pointer' }}>
                            <td>
                              <input type="checkbox" checked={!!selected[r.id]} readOnly />
                            </td>
                            <td>{r.name}</td>
                            <td className="mono">{r.textId || '—'}</td>
                            <td className="mono">{r.plaka || '—'}</td>
                            {/* Kendi kimliği olan bir tüccar bağlanırsa o kimlik PASİFE alınır —
                                kullanıcı neyi kaybettiğini bağlamadan ÖNCE görmeli. */}
                            <td>{r.commercialTitle || '—'}</td>
                            <td className="mono">{r.taxNumber || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
                      <button className="dps-btn primary" disabled={busy || selectedIds.length === 0}
                              onClick={() => setConfirmLink(true)}>
                        Seçili {selectedIds.length} tüccarı bağla
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <div className="dps-inherit">
              <Icon name="info" size={16} />
              <div>
                Bu temsilci <strong>{hier.parentCommercialTitle || hier.parentRepresentativeName}</strong>’ne
                bağlıdır. Firma ve e-belge ayarları oradan gelir.{' '}
                <a onClick={() => navigate(`/representatives/${hier.parentRepresentativeId}`)}>Ana tüccar kartına git ↗</a>
              </div>
            </div>

            <p className="dps-sub-lbl">Aynı ana kimlik altındaki tüccarlar</p>
            <table className="dps-tbl">
              <thead><tr><th>Temsilci</th><th>Kod</th><th>Plaka</th><th>Rol</th><th /></tr></thead>
              <tbody>
                {hier.linked.map((r) => {
                  const self = String(r.id) === String(repId);
                  return (
                    <tr key={r.id}>
                      <td>
                        {r.name}
                        {self && <span style={{ color: 'var(--dps-ink-3)' }}> (bu kart)</span>}
                      </td>
                      <td className="mono">{r.textId || '—'}</td>
                      <td className="mono">{r.plaka || '—'}</td>
                      <td>
                        <StatusBadge tone={r.isPrimaryIdentity ? 'ok' : 'neutral'}>
                          {r.isPrimaryIdentity ? 'Ana Tüccar' : 'Alt Tüccar'}
                        </StatusBadge>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {!self && (
                          <button className="dps-btn ghost sm" onClick={() => navigate(`/representatives/${r.id}`)}>Karta git ↗</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </Section>

      {confirmLink && (
        <SubModal
          title="Ticari Kimliğe Bağla" maxWidth={520} onClose={() => setConfirmLink(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setConfirmLink(false)}>İptal</button>
              <button className="dps-btn primary" onClick={doLink} disabled={busy}>Evet, Bağla</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.7 }}>
            Seçtiğiniz <strong>{selectedIds.length} temsilci</strong>, <strong>{repName}</strong> kartındaki
            firma ve e-belge ayarlarıyla belge basacak ve GİB entegrasyonlarını bu bilgilerle yürütecektir.
            <br /><br />
            Bu temsilcilerden birinin kendi firma tanımı varsa <strong>pasife alınır</strong> (silinmez).
          </p>
        </SubModal>
      )}

      {confirmUnlink && (
        <SubModal
          title="Bağı Çöz" maxWidth={520} onClose={() => setConfirmUnlink(null)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setConfirmUnlink(null)}>İptal</button>
              <button className="dps-btn danger" onClick={doUnlink} disabled={busy}>Evet, Bağı Çöz</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.7 }}>
            <strong>{confirmUnlink.name}</strong> bu ticari kimlikten ayrılacak ve <strong>kendi ana kimliği</strong> olacak.
            <br /><br />
            Firma ve e-belge formu <strong>boş açılır</strong>; kendi VKN’si ve entegratör bilgisi tanımlanana kadar
            <strong> belge kesemez</strong>.
          </p>
        </SubModal>
      )}

    </div>
  );
}

export default LinkedTuccarsTab;
